/**
 * HowItWorksHubPage — the landing page of the How It Works experience.
 *
 * Three chapter cards (The Story / The Workflow / Around the Site) on the
 * shared dark tour backdrop. Picking a card is the user gesture that
 * unlocks audio, so chapters open with music already playing. Escape or X
 * here leaves the experience entirely (back to the dashboard).
 */
import { Close } from '@mui/icons-material';
import { Box, ButtonBase, IconButton, Tooltip, Typography } from '@mui/material';
import { alpha, keyframes, useTheme } from '@mui/material/styles';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { HOW_IT_WORKS_SEEN_KEY } from '../../constants/config';
import { ROUTES } from '../../constants/routes';
import { TourAudioControls } from './TourAudioControls';
import { FloatingParticles } from './TourEffects';
import { tourBackdropSx } from './tourCore';
import { TOUR_SLIDES } from './tourSlides';
import { WORKFLOW_SLIDES } from './workflowSlides';
import { SITE_SLIDES } from './siteSlides';
import type { TourAudio } from './useTourAudio';
import AlisWordmark from '../../components/brand/AlisWordmark';

const riseIn = keyframes`
  from { opacity: 0; transform: translateY(26px); }
  to   { opacity: 1; transform: translateY(0); }
`;

interface ChapterCard {
  route: string;
  kicker: string;
  title: string;
  blurb: string;
  thumb: string;
  slideCount: number;
}

const CHAPTERS: ChapterCard[] = [
  {
    route: ROUTES.HOW_IT_WORKS_STORY,
    kicker: 'Chapter 1',
    title: 'The Story',
    blurb: 'Why ALIS exists — from five scattered tools to one home for the whole journey.',
    thumb: '/how-it-works/img/hero.webp',
    slideCount: TOUR_SLIDES.length,
  },
  {
    route: ROUTES.HOW_IT_WORKS_WORKFLOW,
    kicker: 'Chapter 2',
    title: 'The Workflow',
    blurb: 'The big picture: every path work can take, and how it all stays connected.',
    thumb: '/how-it-works/img-workflow/map.webp',
    slideCount: WORKFLOW_SLIDES.length,
  },
  {
    route: ROUTES.HOW_IT_WORKS_SITE_TOUR,
    kicker: 'Chapter 3',
    title: 'Around the Site',
    blurb: 'A guided walk through the real pages — where everything is and what it does.',
    thumb: '/how-it-works/img-site/dashboard.webp',
    slideCount: SITE_SLIDES.length,
  },
];

interface HowItWorksHubPageProps {
  audio: TourAudio;
}

export const HowItWorksHubPage = ({ audio }: HowItWorksHubPageProps) => {
  const theme = useTheme();
  const navigate = useNavigate();

  // Remember that this user has explored the experience (used by the
  // go-live invite later; the auto-popup itself was removed pre-launch).
  useEffect(() => {
    try {
      localStorage.setItem(HOW_IT_WORKS_SEEN_KEY, new Date().toISOString());
    } catch {
      /* private mode */
    }
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') navigate(ROUTES.DASHBOARD);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [navigate]);

  const openChapter = (route: string) => {
    audio.unlock();
    audio.play('click');
    navigate(route);
  };

  return (
    <Box sx={tourBackdropSx(theme)}>
      <FloatingParticles />

      {/* Top chrome — sits ABOVE the full-screen content overlay (zIndex),
          but lets clicks in its empty middle fall through (pointerEvents). */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1,
          pointerEvents: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: { xs: 2, md: 4 },
          py: 2,
        }}
      >
        <AlisWordmark sx={{ opacity: 0.9 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pointerEvents: 'auto' }}>
          <TourAudioControls audio={audio} />
          <Tooltip title="Back to the app">
            <IconButton
              onClick={() => navigate(ROUTES.DASHBOARD)}
              sx={{ color: alpha(theme.palette.common.white, 0.7), ml: 0.5 }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Content */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 2.5, md: 6 },
          pt: 8,
          pb: 4,
          overflowY: 'auto',
        }}
      >
        <Typography
          sx={{
            animation: `${riseIn} 0.6s ease-out both`,
            color: theme.schedule.tentative,
            filter: 'brightness(1.35)',
            fontWeight: 700,
            letterSpacing: '0.24em',
            textTransform: 'uppercase',
            fontSize: { xs: '0.7rem', md: '0.8rem' },
            mb: 1.5,
          }}
        >
          Welcome to the lab&rsquo;s new home
        </Typography>
        <Typography
          component="h1"
          sx={{
            animation: `${riseIn} 0.7s 0.1s ease-out both`,
            color: theme.palette.common.white,
            fontWeight: 800,
            letterSpacing: '-0.02em',
            lineHeight: 1.08,
            fontSize: { xs: '2.1rem', sm: '2.7rem', md: '3.4rem' },
            textAlign: 'center',
            mb: 1.5,
            textWrap: 'balance',
          }}
        >
          How ALIS Works
        </Typography>
        <Typography
          sx={{
            animation: `${riseIn} 0.7s 0.2s ease-out both`,
            color: alpha(theme.palette.common.white, 0.75),
            fontSize: { xs: '0.95rem', md: '1.1rem' },
            textAlign: 'center',
            mb: { xs: 3.5, md: 5 },
          }}
        >
          Three short chapters. Pick one — or take them in order.
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 2, md: 3 },
            width: '100%',
            maxWidth: 1180,
            justifyContent: 'center',
          }}
        >
          {CHAPTERS.map((ch, i) => (
            <ButtonBase
              key={ch.route}
              onClick={() => openChapter(ch.route)}
              focusRipple
              sx={{
                animation: `${riseIn} 0.65s ease-out both`,
                animationDelay: `${0.3 + i * 0.14}s`,
                flex: { xs: '0 0 auto', md: '1 1 0' },
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                textAlign: 'left',
                borderRadius: `${theme.border.radius * 4}px`,
                overflow: 'hidden',
                border: `1px solid ${alpha(theme.palette.common.white, 0.14)}`,
                backgroundColor: alpha(theme.palette.common.white, 0.05),
                boxShadow: `0 16px 48px ${alpha(theme.palette.common.black, 0.4)}`,
                transition: 'transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease',
                '&:hover': {
                  transform: 'translateY(-6px)',
                  borderColor: alpha(theme.schedule.tentative, 0.6),
                  boxShadow: `0 24px 64px ${alpha(theme.palette.common.black, 0.55)}, 0 0 40px ${alpha(theme.palette.primary.main, 0.25)}`,
                },
              }}
            >
              <Box
                component="img"
                src={ch.thumb}
                alt=""
                aria-hidden
                onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                  e.currentTarget.style.display = 'none';
                }}
                sx={{
                  display: 'block',
                  width: '100%',
                  height: { xs: 130, md: 170 },
                  objectFit: 'cover',
                }}
              />
              <Box sx={{ p: { xs: 2, md: 2.5 } }}>
                <Typography
                  sx={{
                    color: theme.schedule.tentative,
                    filter: 'brightness(1.3)',
                    fontWeight: 700,
                    letterSpacing: '0.2em',
                    textTransform: 'uppercase',
                    fontSize: '0.65rem',
                    mb: 0.5,
                  }}
                >
                  {ch.kicker}
                </Typography>
                <Typography sx={{ color: theme.palette.common.white, fontWeight: 800, fontSize: { xs: '1.2rem', md: '1.4rem' }, mb: 0.75 }}>
                  {ch.title}
                </Typography>
                <Typography sx={{ color: alpha(theme.palette.common.white, 0.72), fontSize: '0.85rem', lineHeight: 1.55, mb: 1.25 }}>
                  {ch.blurb}
                </Typography>
                <Typography sx={{ color: alpha(theme.palette.common.white, 0.45), fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.06em' }}>
                  {ch.slideCount} slides · ~2 min
                </Typography>
              </Box>
            </ButtonBase>
          ))}
        </Box>
      </Box>
    </Box>
  );
};
